import React, { useState, useRef } from 'react';
import { Modal, Input, Button, Spin, message, Alert, Typography } from 'antd';
import { EditOutlined, CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { getIntroRequestDraft } from '../../services/api';
import { captureEvent } from '../../services/analytics';
import DraftLanguageChip from '../localization/DraftLanguageChip';
import css from './IntroRequestModal.module.scss';

const { TextArea } = Input;
const { Text } = Typography;

interface DraftLanguageMeta {
  language: string;
  languageName: string;
  reason: string;
}

interface IntroRequestModalProps {
  opportunityId: string;
  companyName: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Ask-for-a-vouch draft: a note to the user's connection + a factual blurb the
 * connection can forward. Stateless server-side — nothing is stored; regenerate
 * and retry-after-500 are the same call. We never send anything; both outputs
 * are copy-and-send-yourself. The blurb is deliberately labeled "lines they can
 * forward" — never a recommendation (the vouch is the connection's to add).
 *
 * No destroyOnClose: while the drawer stays open, reopening the panel shows the
 * last draft instead of burning another LLM call. The parent keys this modal by
 * opportunity id, so switching opportunities starts clean.
 */
const IntroRequestModal: React.FC<IntroRequestModalProps> = ({
  opportunityId,
  companyName,
  open,
  onClose,
}) => {
  const [connectionName, setConnectionName] = useState('');
  const [relationshipNote, setRelationshipNote] = useState('');
  const [generating, setGenerating] = useState(false);
  // Boolean only — the Alert shows fixed copy, never the server message.
  const [error, setError] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [blurbText, setBlurbText] = useState('');
  const [hasDraft, setHasDraft] = useState(false);
  const [languageMeta, setLanguageMeta] = useState<DraftLanguageMeta | null>(null);
  // Snapshot of the last generated draft, to warn before regenerate overwrites edits.
  const pristineRef = useRef<{ note: string; blurb: string }>({ note: '', blurb: '' });

  const runGenerate = async () => {
    // No mountedRef (unlike ComposeEmailModal) — deliberate: with no
    // destroyOnClose this instance stays mounted across close/reopen, so a
    // late result landing after close IS the designed persistence; switching
    // opportunities remounts via the parent's key; and React 18 no-ops
    // setState after a real unmount anyway.
    const isRegenerate = hasDraft;
    setGenerating(true);
    setError(false);
    try {
      const draft = await getIntroRequestDraft(opportunityId, {
        connectionName: connectionName.trim() || undefined,
        relationshipNote: relationshipNote.trim() || undefined,
      });
      setNoteText(draft.message);
      setBlurbText(draft.blurb);
      pristineRef.current = { note: draft.message, blurb: draft.blurb };
      setLanguageMeta({
        language: draft.language,
        languageName: draft.languageName,
        reason: draft.reason,
      });
      setHasDraft(true);
      captureEvent('intro_request_generated', {
        has_name: !!connectionName.trim(),
        has_relationship: !!relationshipNote.trim(),
        is_regenerate: isRegenerate,
        language: draft.language,
      });
    } catch (err) {
      setError(true);
      captureEvent('intro_request_draft_failed', {});
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = () => {
    const dirty =
      hasDraft &&
      (noteText !== pristineRef.current.note || blurbText !== pristineRef.current.blurb);
    if (dirty) {
      Modal.confirm({
        title: 'Replace your edits?',
        content: 'Regenerating writes a fresh draft and discards your current edits. Continue?',
        okText: 'Regenerate',
        cancelText: 'Keep my edits',
        onOk: runGenerate,
      });
    } else {
      runGenerate();
    }
  };

  const handleCopy = async (part: 'message' | 'blurb') => {
    const text = part === 'message' ? noteText : blurbText;
    try {
      await navigator.clipboard.writeText(text);
      message.success('Copied to clipboard');
      captureEvent('intro_request_copied', { part });
    } catch {
      message.error('Could not copy — select the text and copy manually');
    }
  };

  return (
    <Modal
      title={
        <div className={css.modalTitle}>
          <EditOutlined />
          <span>Draft an intro request — {companyName}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(640px, 94vw)"
      className={css.introModal}
    >
      <p className={css.explainer}>
        Know someone at {companyName}?
        <br />
        We'll draft a short note asking them to put in a word — edit it before you send.
      </p>

      <div className={css.inputsRow}>
        <div className={css.inputField}>
          <label className={css.label} htmlFor="intro-connection-name">
            Their first name (optional)
          </label>
          <Input
            id="intro-connection-name"
            value={connectionName}
            maxLength={100}
            onChange={(e) => setConnectionName(e.target.value)}
            placeholder="Jane"
            disabled={generating}
          />
        </div>
        <div className={css.inputField}>
          <label className={css.label} htmlFor="intro-relationship">
            How you know them (optional)
          </label>
          <Input
            id="intro-relationship"
            value={relationshipNote}
            maxLength={200}
            onChange={(e) => setRelationshipNote(e.target.value)}
            placeholder="worked together at Acme"
            disabled={generating}
          />
        </div>
        <Button
          type="primary"
          onClick={handleGenerate}
          loading={generating}
          icon={hasDraft ? <ReloadOutlined /> : undefined}
          className={css.generateButton}
        >
          {hasDraft ? 'Regenerate' : 'Generate'}
        </Button>
      </div>

      {generating && !hasDraft && (
        <div className={css.loadingContainer}>
          <Spin size="large" />
          <p>Drafting your ask…</p>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          className={css.errorAlert}
          message="Couldn't draft it just now"
          description="Nothing was lost — try again."
          action={
            // Deliberately bypasses the dirty-guard: a retry continues the
            // regenerate the user already consented to.
            <Button size="small" onClick={runGenerate}>
              Try again
            </Button>
          }
        />
      )}

      {hasDraft && (
        <div className={css.drafts}>
          {languageMeta && (
            <DraftLanguageChip
              language={languageMeta.language}
              languageName={languageMeta.languageName}
              reason={languageMeta.reason}
            />
          )}
          <div className={css.field}>
            <div className={css.labelRow}>
              <label className={css.label} htmlFor="intro-note-text">The note to send them</label>
              <Button
                size="small"
                icon={<CopyOutlined />}
                disabled={generating}
                onClick={() => handleCopy('message')}
              >
                Copy
              </Button>
            </div>
            <TextArea
              id="intro-note-text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={7}
              dir="auto"
              disabled={generating}
              className={css.bodyInput}
            />
          </div>
          <div className={css.field}>
            <div className={css.labelRow}>
              <label className={css.label} htmlFor="intro-blurb-text">A few lines they can forward</label>
              <Button
                size="small"
                icon={<CopyOutlined />}
                disabled={generating}
                onClick={() => handleCopy('blurb')}
              >
                Copy
              </Button>
            </div>
            <TextArea
              id="intro-blurb-text"
              value={blurbText}
              onChange={(e) => setBlurbText(e.target.value)}
              rows={4}
              dir="auto"
              disabled={generating}
              className={css.bodyInput}
            />
          </div>
        </div>
      )}

      <div className={css.footerNote}>
        <Text type="secondary">We don't send anything — copy it and send it yourself.</Text>
      </div>
    </Modal>
  );
};

export default IntroRequestModal;
